import * as Api from '@ViewModels';

interface ICanEditAutomationTemplateParams {
	template: Pick<Api.IAutomationTemplateReference, 'scope' | 'status'>;
	userRole?: Api.UserRole;
	impersonationContext?: Api.IImpersonationContext;
	/** When true, allows editing Industry-scoped (sample) templates from the platform admin panel */
	isAdminContext?: boolean;
}

export function canEditAutomationTemplate({
	template,
	userRole,
	impersonationContext,
	isAdminContext,
}: ICanEditAutomationTemplateParams) {
	if (template.status === Api.TemplateStatus.Archived) {
		return false;
	}

	// Allow editing Industry (sample) templates from the platform admin panel
	if (template.scope === Api.TemplateScope.Industry) {
		return !!isAdminContext;
	}

	return isAccountAdmin(userRole, impersonationContext) || template.scope === Api.TemplateScope.User;
}

export function canRestoreAutomationTemplate({
	template,
	userRole,
	impersonationContext,
}: Omit<ICanEditAutomationTemplateParams, 'isAdminContext'>) {
	return isRestorableArchivedTemplate(template) && isAccountAdmin(userRole, impersonationContext);
}

function isAccountAdmin(userRole?: Api.UserRole, impersonationContext?: Api.IImpersonationContext) {
	const isAdminRole = (role?: Api.UserRole) => role === Api.EUserRole.SuperAdmin || role === Api.EUserRole.Admin;
	return isAdminRole(userRole) || isAdminRole(impersonationContext?.user?.role);
}

/** The impersonated-route counterpart of `canRestoreAutomationTemplate`, gated on the same on-behalf permission as editing. */
export function canRestoreAutomationTemplateOnBehalfOfAccount({
	template,
	hasOnBehalfPermission,
}: {
	template: Pick<Api.IAutomationTemplateReference, 'scope' | 'status'>;
	hasOnBehalfPermission: boolean;
}) {
	return hasOnBehalfPermission && isRestorableArchivedTemplate(template);
}

// Catalog (system and industry) templates are refused by the API: they aren't restored from inside an account.
function isRestorableArchivedTemplate(template: Pick<Api.IAutomationTemplateReference, 'scope' | 'status'>) {
	return (
		template.status === Api.TemplateStatus.Archived &&
		template.scope !== Api.TemplateScope.System &&
		template.scope !== Api.TemplateScope.Industry
	);
}

/**
 * Whether a platform admin working inside a customer account (impersonated routes) can edit a template.
 * Industry samples are excluded: they live in Levitate's account, so the account-scoped impersonated
 * writes (e.g. `impersonate/{accountId}/AutomationTemplate/{id}/Draft/commit`) 404 on them. Samples are
 * edited from the sample editor, or duplicated into the account first.
 */
export function canEditAutomationTemplateOnBehalfOfAccount({
	template,
	hasOnBehalfPermission,
}: {
	template: Pick<Api.IAutomationTemplateReference, 'scope' | 'status'>;
	hasOnBehalfPermission: boolean;
}) {
	return (
		hasOnBehalfPermission &&
		template.status !== Api.TemplateStatus.Archived &&
		template.scope !== Api.TemplateScope.Industry
	);
}
